import React, { useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import Colors from "../../../ui/global/Colors";
import Fonts from "../../../ui/global/Fonts";
import Icon from "../../../ui/global/Icon";
import InputSimple from "../../../ui/components/InputSimple";
import Button from "../../../ui/components/Button";
import Tooltip from "../../../ui/components/Tooltip";
import { ArtType } from "../Art";
import { useCreditsContext } from "../../pricing/CreditsContext";

const Wrapper = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    width: 100%;
    gap: 10px;
`;

const DescriptionContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    font-family: ${Fonts.lexendMedium.fontFamily};
    font-weight: ${Fonts.lexendMedium.fontWeight};
    font-size: 18px;
    line-height: 1.2em;
    border-radius: 12px;
    background-color: ${Colors.transparent};
    border: 1px solid ${Colors.transparent};
    // padding: 2px 4px 2px 10px;
    padding: 2px 4px;
    color: ${Colors.white};
    transition: all 0.2s ease;

    &:hover {
        // background-color: ${Colors.white};
        // border: 1px solid ${Colors.grey2};
    }

    @media (max-width: 768px) {
        font-size: 16px;
    }
`;

const blink = keyframes`
  0% {
    transform: translateY(-50%) scale(0.8);
  }
  50% {
    transform: translateY(-50%) scale(1.2);
  }
  100% {
    transform: translateY(-50%) scale(0.8);
  }
`;

const Tutorial = styled.div`
    position: absolute;
    left: -42px;
    top: 40%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    animation: ${blink} 1s infinite ease-in-out;

    @media (max-width: 450px) {
        display: none;
    }
`;

export interface ArtDescriptionProps {
  config: any;
  artType: ArtType;
  description: string;
  onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onGenerateClick: () => void;
  isGeneratingImage: boolean;
  hasDrawing: boolean;
}

const ArtDescription = ({
  config,
  artType,
  description,
  onChange,
  onGenerateClick,
  isGeneratingImage,
  hasDrawing,
}: ArtDescriptionProps) => {
  const { isOutOfCredits } = useCreditsContext();
  const descriptionTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleDescriptionChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value;
    onChange(event);
    
    // Clear any existing timeout
    if (descriptionTimeoutRef.current) {
      clearTimeout(descriptionTimeoutRef.current);
    }
  };

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (descriptionTimeoutRef.current) {
        clearTimeout(descriptionTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Wrapper>
        {description === '' && (
            <Tutorial>
                <Icon 
                    icon="arrow-down-right" 
                    color={Colors.black} 
                    size={34} 
                />
            </Tutorial>
        )}
        <DescriptionContainer id='description-container'>
            <InputSimple 
                placeholder="Describe your drawing..." 
                value={description} 
                textAlign="left"
                padding="6px 0"
                onChange={handleDescriptionChange} 
                onEnterPress={onGenerateClick}
            />
            {!config.ART_REALTIME && artType === '2D' && (
                <Tooltip text="Create Image" colorMode="dark" position="top">
                    <Button
                        size="tiny"
                        color="black"
                        icon="sparkle"
                        onClick={onGenerateClick}
                        disabled={(!hasDrawing && description === '') || isGeneratingImage || isOutOfCredits.art_2d_limited}
                    />
                </Tooltip>
            )}
        </DescriptionContainer>
    </Wrapper>
  );
};

export default ArtDescription;